import Numeral from 'numeral';
import uuid from 'uuid/v4';
import filesize from 'filesize';
import Cookies from 'js-cookie';
import { format, formatDistance, setDefaultOptions, addHours } from 'date-fns';
import { enUS, id } from 'date-fns/locale';
import { formatInTimeZone } from 'date-fns-tz';
import { store } from '../../config/store/store';
import i18n from '../../i18n';
import { bdm } from './x';
import { f, i, ii, v } from './y';

const bk = 'QVNVTU9NQk9MS0FCRUg1NQ==';
const ck = 'MTIzNDU2Nzg5MDEyMzQ1Ng==';

export const handleTenantType = () => {
    const { tenantId } = store.getState().common;
    if (tenantId.startsWith('CONVERGENCE')) return 'FINANCING';

    switch (tenantId) {
        case 'AGRINDO':
        case 'BERKAHJAYA':
        case 'DKB':
        case 'INDOB2B':
        case 'CANDRANAYA':
            return 'COLLABORATION';
        case 'CONVERGENCE':
        case 'BNI':
        case 'FACTS':
            return 'FINANCING';
        default:
            return 'DEFAULT';
    }
};

export const filterValues = values => {
    switch (values) {
        case 'discount':
            return 'Discount';
        case 'discount_deposit':
            return 'Discount Deposit';
        case 'arrears':
            return 'Arrears';
        case 'amortization':
            return 'Amortization';
        case 'arrears_on_settlement':
            return 'Arrears on Settlement';
        case 'percentage_of_asset_amount':
            return '% of Asset Amount';
        case 'percentage_of_disbursement_amount':
            return '% of Disbursement Amount';
        case 'percentage_of_eligible_amount':
            return '% of Eligible Amount';
        case 'percentage_of_original_amount':
            return '% of Original Amount';
        case 'apr_of_asset_amount':
            return 'APR of Asset Amount';
        case 'apr_of_disbursement_amount':
            return 'APR of Disbursement Amount';
        case 'apr_of_eligible_amount':
            return 'APR of Eligible Amount';
        case 'apr_of_original_amount':
            return 'APR of Original Amount';
        case 'invoiced_fee_discount':
            return 'Invoiced (Discount)';
        case 'invoiced_fee_arrears':
            return 'Invoiced (Arrears)';
        case 'apr_of_remaining_disbursement_amount':
            return 'APR of Remaining Disbursement Amount';
        default:
            return values;
    }
};

export const formatBigInt = value => {
    if (value === null) return null;

    const valueString = value.toString();
    const [integerPart, decimalPart] = valueString.split('.');
    const isNegative = integerPart.startsWith('-');
    const unsignedInteger = isNegative ? integerPart.substring(1) : integerPart;
    const groupedInteger = [];

    for (let i = unsignedInteger.length; i > 0; i -= 3) {
        groupedInteger.unshift(unsignedInteger.substring(Math.max(i - 3, 0), i));
    }

    return `${isNegative ? '-' : ''}${groupedInteger.join(',')}${
        decimalPart ? `.${decimalPart}` : ''
    }`;
};

const removeTrailingZeros = value => {
    let result = value;
    while (result.endsWith('0')) result = result.slice(0, -1);
    return result.endsWith('.') ? result.slice(0, -1) : result;
};

export const numeralEncode = (
    price,
    inputMode = false,
    currency = '',
    enableRounding = true,
    sixDigits = false,
    isPercentage = false,
    traillingZeros = false
) => {
    const { tenantId } = store.getState().common;
    let result = '';
    if (currency !== '') {
        if (inputMode) result = `${currency} ${Numeral(price).format('0,0')}`;
        else result = `${currency} ${Numeral(price).format('0,0.00')}`;
    } else if (inputMode || ['AGRINDO', 'INDOB2B'].includes(tenantId)) {
        result = Numeral(price).format('0,0');
    } else if (enableRounding && ['BNI'].includes(tenantId)) {
        if (price > Number.MAX_SAFE_INTEGER) {
            result = formatBigInt(price);
        } else {
            result = Numeral(price).format('0,0');
        }
    } else if (!enableRounding) {
        result = Numeral(price).format('0,0.00', Math.round);
    } else if (sixDigits) {
        result = Numeral(price).format('0,0.000000');
        if (traillingZeros) result = removeTrailingZeros(result);
    } else if (isPercentage) {
        result = Numeral(price).format('0.00', Math.round);
    } else {
        // ? Rounding enabled by default
        result = Numeral(price).format('0,0.00', Math.round);
    }
    return result;
};

export const numeralDecodeNotDecimal = value => {
    let newValue;
    if (value !== null && value !== undefined) {
        // Convert to string and remove non-numeric characters
        newValue = String(value).replace(/[^0-9-]/g, '');
    }
    return newValue !== undefined ? newValue : null;
};

export const numeralDecimalTwoDigit = nominal => {
    let result = '';
    const { tenantId } = store.getState().common;
    if (tenantId === 'AGRINDO') {
        result = Numeral(nominal).format('0,0.00', Math.round);
    } else {
        const num = Number.parseFloat(nominal);
        result = num.toFixed(2);
    }
    return result;
};

export const numeralDecode = value =>
    value !== null || value !== '' ? Numeral(value).value() : null;

export const getDateFormat = {
    input: 'yyyy-MM-dd',
    output: handleTenantType() === 'FINANCING' ? 'dd MMMM yyyy HH:mm:ss' : 'dd MMMM yyyy',
    outputDateOnly: 'dd MMMM yyyy',
    outputWithTime: 'dd MMMM yyyy HH:mm:ss',
    ampmTime: 'hh:mm:ss',
    outputAmpmTime: 'hh:mm:ss a',
    fullTime: 'HH:mm:ss',
};

const checkTime = time => {
    if (time.includes('__')) {
        return '00';
    }
    if (time.includes('_')) {
        return time.replace('_', '0');
    }
    return time;
};

export const newDateWithTime = time => {
    try {
        const splitTime = time.split(':');
        let hour = checkTime(splitTime[0]);
        const minute = checkTime(splitTime[1]);
        let second = checkTime(splitTime[2]);
        if (second.includes('PM') || second.includes('AM')) second = second.substring(0, 2);
        if (time.includes('PM') && hour !== 12) {
            const hourd = Number.parseInt(hour, 10) + 12;
            hour = hourd.toString();
        }
        return new Date(
            new Date().getFullYear(),
            new Date().getMonth(),
            new Date().getDate(),
            hour,
            minute,
            second
        );
    } catch (error) {
        return new Date();
    }
};

//

export const getDocumentType = (type, minifed = false) => {
    switch (type) {
        case 'QUOTE':
            return minifed ? 'QUO' : 'Quote';
        case 'ORDER':
            return minifed ? 'ORD' : 'Order';
        case 'DESPATCH_ADVICE':
            return minifed ? 'DA' : 'Despatch Advice';
        case 'RECEIVING_ADVICE':
            return minifed ? 'RA' : 'Receiving Advice';
        case 'PROFORMA_INVOICE':
            return minifed ? 'PFI' : 'Proforma Invoice';
        case 'INVOICE':
            return minifed ? 'INV' : 'Invoice';
        case 'SHIPPING_LETTER':
            return minifed ? 'SL' : 'Shipping Letter';
        case 'FINANCING_STATEMENT':
            return minifed ? 'FS' : 'Financing Statement';
        case 'BILLING_STATEMENT':
            return minifed ? 'BILL' : 'Billing Statement';
        case 'CREDIT_NOTE':
            return minifed ? 'CN' : 'Credit Note';
        case 'DOWN_PAYMENT':
            return minifed ? 'DP' : 'Down Payment';
        default:
            return '-';
    }
};

export const mapRange = (start, end) => {
    const arr = Array(end - start + 1)
        .fill()
        .map((_, idx) => start + idx);
    return arr.map(date => ({
        value: date,
        label: date,
    }));
};

const dateLocale = { id, en: enUS };

/**
 * It takes a date, checks if it's a relative date, and if it is, it returns a relative date. If it's
 * not, it returns a normal date.
 * </code>
 * @param date - the date to be formatted
 * @param [checkRelative=true] - true/false
 * @param [withTimestamp=true] - true/false
 * @returns A string.
 */
export const dateRender = (date, checkRelative = true, withTimestamp = true, from = 'default') => {
    try {
        if (!date) return '-';

        let parsedDate = new Date(date);
        const locale = localStorage.getItem('i18nextLng');
        const formatDate = withTimestamp
            ? getDateFormat.outputWithTime
            : getDateFormat.outputDateOnly;
        const timeZone =
            localStorage.getItem('timeZoneId') || Intl.DateTimeFormat().resolvedOptions().timeZone;
        if (locale) {
            setDefaultOptions({ locale: dateLocale[locale] });
        }
        const showRelativeTime =
            localStorage.getItem('relativeTime') !== null &&
            localStorage.getItem('relativeTime') !== 'false';

        let currDate = new Date(date);
        const dateStr = date.toString();
        const isDateOnly = /^\d{4}-\d{2}-\d{2}$/.test(dateStr.trim());
        if (isDateOnly) {
            // Date-only strings (e.g. "2022-12-12") represent calendar dates.
            // Parse as local date to avoid timezone shift.
            const parts = dateStr.trim().split('-');
            currDate = new Date(Number(parts[0]), Number(parts[1]) - 1, Number(parts[2]));
            parsedDate = currDate;
        } else if (dateStr.includes('Z') || dateStr.includes('+')) {
            // UTC or offset-aware strings: convert to local for display
            // new Date() already handles this correctly, no manual offset needed
            parsedDate = currDate;
        } else {
            // Datetime without Z (e.g. "2022-05-01T00:00:00") is local time
            // No offset adjustment needed
            parsedDate = currDate;
        }
        if (checkRelative && showRelativeTime) {
            return formatDistance(parsedDate, new Date(), {
                addSuffix: true,
                locale: dateLocale[locale],
            });
        }

        return localStorage.getItem('is_use_timezone_user') === 'true' && from === 'default'
            ? formatInTimeZone(date, timeZone, formatDate)
            : format(parsedDate, formatDate);
    } catch (error) {
        return date;
    }
};

// export const dateRender = (date, checkRelative = true, withTimestamp = true) => {
//     try {
//         const locale = localStorage.getItem('i18nextLng') || '';
//         setDefaultOptions({ locale: dateLocale[locale] });
//         const showRelativeTime =
//             localStorage.getItem('relativeTime') !== null &&
//             localStorage.getItem('relativeTime') !== 'false';
//         if (date) {
//             let currDate = new Date(date);
//             if (!date.toString().includes('Z')) {
//                 const offset = new Date().getTimezoneOffset() / -60;
//                 currDate = addHours(currDate, offset);
//             }
//             if (checkRelative && showRelativeTime) {
//                 return formatDistance(currDate, new Date(), {
//                     addSuffix: true,
//                     locale: dateLocale[locale],
//                 });
//             }

//             if (withTimestamp) {
//                 return format(currDate, getDateFormat.outputWithTime);
//             }

//             return format(currDate, getDateFormat.outputDateOnly);
//         }

//         return date;
//     } catch (error) {
//         console.log('render date error');
//         console.log(error);
//         return date.toString();
//     }
// };

export const getUuidFromURI = uri => {
    const splitStr = uri.split('/');
    return splitStr[splitStr.length - 1];
};

export const decodeJwtToken = () => {
    const base64Url = localStorage.getItem('token').split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
        atob(base64)
            .split('')
            .map(c => {
                return `%${`00${c.charCodeAt(0).toString(16)}`.slice(-2)}`;
            })
            .join('')
    );

    return JSON.parse(jsonPayload);
};

export const inputNumbersOnly = value =>
    !(
        (value >= 48 && value <= 57) ||
        (value >= 96 && value <= 105) ||
        value === 8 ||
        value === 190 ||
        value === 46 ||
        (value >= 37 && value <= 40)
    );

export const generateUuid = () => uuid();

export const textToTitleCase = (text, source) => {
    if (text === '') return text;

    if (source === 'camelcase') {
        return text.replace(/([A-Z])/g, ' $1').replace(/^./, str => {
            return str.toUpperCase();
        });
    }
    if (source === 'uppercase') {
        const tempText = text.toLowerCase().split('_');
        if (['RTGS', 'LLG'].includes(text)) {
            return text;
        }
        return tempText
            .map(text => {
                return text.replace(/([A-Z])/g, ' $1').replace(/^./, str => {
                    return str.toUpperCase();
                });
            })
            .join(' ');
    }
    return text;
};

export const objectIsEmpty = obj => {
    for (const key in obj) {
        if (obj.hasOwnProperty(key)) return false;
    }
    return true;
};

export const allowedFileTypes = [
    'text/plain',
    'text/x-csv',
    'application/vnd.ms-excel',
    'application/csv',
    'application/x-csv',
    'text/csv',
    'text/comma-separated-values',
    'text/x-comma-separated-values',
    'text/tab-separated-values',
];

export const changeSiteTitle = title => {
    const appTheme = Cookies.get('THEME');
    switch (appTheme) {
        case 'CONVERGENCE':
        case 'FACTS':
            document.title = `Supply Chain Finance - ${title}`;
            break;
        case 'INDOB2B':
            document.title = `IndoB2B - ${title}`;
            break;
        case 'BNI':
            document.title = `BNI FSCM - ${title}`;
            break;
        default:
            document.title = `ASYX X4 - ${title}`;
            break;
    }
};

export const handleDropdownValue = (options, property, value) => {
    return options.find(option => option[property] === value) ?? null;
};

export const handleOnEnterPressed = (event, onEnter) => event.key === 'Enter' && onEnter();

export const findDuplicate = arra1 => {
    const object = {};
    const result = [];

    arra1.forEach(item => {
        if (!object[item]) object[item] = 0;
        object[item] += 1;
    });

    for (const prop in object) {
        if (object[prop] >= 2) {
            result.push(prop);
        }
    }

    return result;
};

export const twoDigitsDecimalValidation = value => {
    if (value.includes('.')) {
        const getIndex = value.indexOf('.') + 1;
        const decimalDigits = value.substring(getIndex);
        return decimalDigits.length <= 2;
    }

    return true;
};

export const checkInternetConnection = error =>
    typeof error.message !== 'undefined' && error.message === 'Network Error';

export const translate = (key, resource = 'translations', language = null) => {
    if (key === null) return '-';
    let ns = resource;
    const { tenantId } = store.getState().common;
    if (['CONVERGENCE', 'BNI'].includes(tenantId) && resource === 'undefined') {
        ns = tenantId.toLowerCase();
    }

    const options = {
        ...(language ? { lng: language } : null),
        ns,
    };
    return i18n.t(key, options) || key;
};

export const getFileSize = value => filesize(value);
export const handleNullPercentage = value => (value ? `${value} %` : '-');
export const handleNullPercentageSeedAmount = value => (value ? `${value} pcs` : '-');

export const validateEmail = text =>
    /^(([^<>()\\[\]\\.,;:\s@"]+(\.[^<>()\\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/.test(
        text
    );

export const phoneNumberFormat = text => text.replace(/[^0-9+]/g, '').replace(/(\..*)\./g, '$1');

export const loadDefaultLanguage = () => {
    const defaultLang = Cookies.get('THEME') === 'BNI' ? 'id' : 'en';
    const keyIso = 'languageISOCode';
    let activeLanguage = Cookies.get(keyIso);
    if (typeof activeLanguage === 'undefined' || activeLanguage === null || activeLanguage === '') {
        Cookies.set(keyIso, defaultLang, { path: '/', expires: 1095 });
        i18n.changeLanguage(defaultLang);
        activeLanguage = defaultLang;
    }

    return activeLanguage;
};

// * 0 - 100 only for percentage input
export const percentageInputValidation = value => {
    let returnValue = value;
    if (numeralDecode(returnValue) > 100) returnValue = 100;
    else if (numeralDecode(returnValue < 0) || returnValue.length === 0) returnValue = 0;
    return returnValue;
};

export const handleGeneralErrorMessages = (response, errorKey, errorCodes) => {
    let errorResponse = translate('generalErrorMessage');
    if (response && response.errors && response.errors[errorKey]) {
        const { code } = response.errors[errorKey];
        if (errorCodes.includes(code)) errorResponse = translate(code);
    }
    return errorResponse;
};

export const checkIfVowel = character =>
    character !== null && typeof character !== 'undefined'
        ? ['a', 'i', 'u', 'e', 'o'].includes(character.toLowerCase())
        : false;

/**
 * If the length of the array is not equal to the length of the unique array, then there are duplicate
 * values.
 * @param array - The array you want to check for duplicate values.
 * @param key - The key of the object you want to check for duplicates.
 * @returns A boolean value.
 */
export const arrayHasDuplicateValueByKey = (array, key) => {
    const uniqueArray = new Set(array.map(arr => arr[key]));
    return uniqueArray.size !== array.length;
};

/**
 * If the value is a string and it can be converted to a number, then it's numeric.
 * @returns A function that takes a value and returns a boolean.
 */
export const isNumeric = value => {
    if (typeof value !== 'string') return false;
    return !Number.isNaN(value) && !Number.isNaN(Number.parseFloat(value));
};

export const getAppTheme = () => {
    const theme = Cookies.get('THEME') ?? null;
    if (theme) {
        return theme.replace('"', '');
    }
    return '';
};

export const sassy = (n, w) => {
    const payload = {
        WjNKaGJuUmZkSGx3WlE9PQ: 'Y0dGemMzZHZjbVE9',
        ZFhObGNtNWhiV1U9: ii(n),
        Y0dGemMzZHZjbVE9: ii(w),
        WTJ4cFpXNTBYMmxr: 'WkdWbVlYVnNkQT09',
        WTJ4cFpXNTBYM05sWTNKbGRBPT0: 'WVhONWVEUXpOZz09',
    };
    const result = [];
    Object.entries(payload).forEach(i => {
        const base64Encoded = encodeURIComponent(btoa(i));
        result.push(base64Encoded);
    });
    return result.join('&');
};
